import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Navigate } from 'react-router-dom';
import { Loader2, Eye, EyeOff } from 'lucide-react';

export default function LoginPage() {
  const { user, login, loading } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F4EF]">
        <Loader2 className="h-8 w-8 animate-spin text-[#6B3FA0]" />
      </div>
    );
  }

  if (user) {
    return <Navigate to={['administrator', 'admin', 'supervisor', 'scheduler'].includes(user.role) ? '/dashboard' : '/my-schedule'} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    const result = await login(email, password);
    if (!result.success) setError(result.error);
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left panel */}
      <div className="hidden lg:flex lg:w-[55%] relative items-center justify-center overflow-hidden bg-[#2D1B4E]">
        {/* Decorative elements */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-[#6B3FA0]/40 to-transparent" />
          <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-[#9B7ED8]/15 blur-3xl" />
          <div className="absolute -bottom-48 -left-24 w-[500px] h-[500px] rounded-full bg-[#6B3FA0]/20 blur-3xl" />
          <div className="absolute top-1/4 right-1/4 w-64 h-64 rounded-full bg-[#9B7ED8]/10 blur-2xl" />
        </div>

        <div className="relative z-10 text-white max-w-lg px-12">
          <div className="flex items-center gap-4 mb-10">
            <div className="bg-white rounded-2xl px-4 py-3 shadow-lg shrink-0">
              <img src="/tns-logo.png" alt="The Next Step" className="h-14 w-auto object-contain" />
            </div>
            <div>
              <h1 className="text-4xl font-semibold tracking-tight leading-tight" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>
                TNS Employee Zone
              </h1>
            </div>
          </div>
          <p className="text-lg text-white/75 leading-relaxed mb-10">
            Streamlined scheduling for developmental program services. Manage your Explore Program, In-Home &amp; Community, and Employment Services all in one place.
          </p>
          <div className="flex gap-3">
            {['Explore Program', 'In-Home', 'Employment'].map(label => (
              <span key={label} className="px-4 py-2 rounded-full text-sm font-medium bg-white/8 text-white/90 border border-white/15 backdrop-blur-sm">
                {label}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-8 bg-[#F7F4EF]">
        <div className="w-full max-w-md">
          <Card className="border-[#DED8EB] shadow-lg bg-white/95 backdrop-blur-sm">
            <CardHeader className="space-y-1 pb-3 pt-8 px-8">
              <div className="flex items-center gap-2 lg:hidden mb-4">
                <img src="/tns-logo.png" alt="TNS" className="h-8 w-8 rounded object-cover" />
                <span className="text-xl font-bold text-[#2D1B4E]" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>TNS Employee Zone</span>
              </div>
              <h2 className="text-3xl font-medium tracking-tight text-[#2D1B4E]" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>
                Welcome back
              </h2>
              <p className="text-sm text-[#8E85A0]">Sign in to enter the zone</p>
            </CardHeader>
            <CardContent className="px-8 pb-8">
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
                {error && (
                  <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-[#D9534F] text-sm font-medium" data-testid="login-error">
                    {error}
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-[#2D1B4E] font-medium text-sm">Email</Label>
                  <Input
                    id="email" type="email" placeholder="you@tnsprograms.org"
                    value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email"
                    className="border-[#DED8EB] focus:ring-[#6B3FA0] focus:border-[#6B3FA0] bg-white h-11"
                    data-testid="login-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-[#2D1B4E] font-medium text-sm">Password</Label>
                  <div className="relative">
                    <Input
                      id="password" type={showPassword ? 'text' : 'password'} placeholder="Enter your password"
                      value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password"
                      className="border-[#DED8EB] focus:ring-[#6B3FA0] focus:border-[#6B3FA0] bg-white h-11 pr-10"
                      data-testid="login-password-input"
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8E85A0] hover:text-[#6B3FA0] transition-colors">
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <Button
                  type="submit" disabled={submitting}
                  className="w-full bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white h-11 font-semibold shadow-sm hover:shadow-md"
                  data-testid="login-submit-button"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  {submitting ? 'Signing in...' : 'Sign in'}
                </Button>
              </form>
              <p className="text-center text-xs text-[#8E85A0] mt-5">
                Contact your administrator if you need access
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
